/* Mobile frame behaviour: <mw-mobile-header>, <mw-mobile-menu> and <mw-mobile-dock>.
   Loaded by the three mobile sections. Server Liquid is the truth; this module only adds
   behaviour and never renders content.

   What lives here and why:
   - the mobile header owns --header-height / --header-offset / --announcement-height while it
     is the visible header (theme.js's <mw-header> stands down when its own bar is display:none)
   - the mobile menu is a panel stack inside ONE overlay (assets/overlay.js): focus trap,
     Escape, outside click, scroll lock and one-at-a-time are not reimplemented here
   - the dock measures itself into --mw-dock-h so the page can reserve room at its end, and
     falls back gracefully when the Mobile menu section is not on the page. */

const root = document.documentElement;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const setPx = (name, value) => root.style.setProperty(name, `${Math.round(value)}px`);
const rendered = (el) => Boolean(el && el.getClientRects().length);

/* ---------- Mobile header ---------- */
class MwMobileHeader extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.mw-mheader__bar');
    if (!this.bar) return;
    this.host = this.closest('.shopify-section') || this;
    this.sticky = this.dataset.sticky || 'scroll_up';
    this.offset = -1;
    this.lastY = window.scrollY;
    this.onScroll = this.onScroll.bind(this);
    this.onEditorChange = this.onEditorChange.bind(this);

    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.observe();
    this.measure();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onEditorChange);
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((type) => {
      document.addEventListener(type, this.onEditorChange);
    });
    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onEditorChange);
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((type) => {
      document.removeEventListener(type, this.onEditorChange);
    });
    this.resizeObserver?.disconnect();
  }

  /* Header-group sections rendered before this one (announcement bars). */
  sectionsAbove() {
    const sections = [];
    for (let el = this.host.previousElementSibling; el; el = el.previousElementSibling) {
      if (el.classList.contains('shopify-section')) sections.push(el);
    }
    return sections;
  }

  observe() {
    this.resizeObserver.disconnect();
    this.resizeObserver.observe(this);
    this.sectionsAbove().forEach((section) => this.resizeObserver.observe(section));
  }

  onEditorChange() {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.observe();
      this.measure();
    });
  }

  measure() {
    /* Above the breakpoint this section is display:none and <mw-header> owns the geometry. */
    if (!rendered(this.bar)) return;
    const above = this.sectionsAbove().reduce((sum, section) => sum + section.offsetHeight, 0);
    this.threshold = above + 8;
    setPx('--announcement-height', above);
    setPx('--header-height', this.bar.offsetHeight);
    this.updateOffset();
  }

  updateOffset() {
    if (!rendered(this.bar)) return;
    const isSticky = /sticky|fixed/.test(getComputedStyle(this.host).position);
    const offset = isSticky ? Math.max(0, Math.round(this.bar.getBoundingClientRect().bottom)) : 0;
    if (offset === this.offset) return;
    this.offset = offset;
    setPx('--header-offset', offset);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      if (!rendered(this.bar)) return;
      const y = Math.max(0, window.scrollY);
      const past = y > (this.threshold ?? 8);
      this.classList.toggle('is-scrolled', past);

      if (this.sticky === 'scroll_up') {
        const down = y > this.lastY + 4;
        const up = y < this.lastY - 4;
        const hidden = this.classList.contains('is-hidden');
        if (down && past && !hidden) this.follow();
        else if ((up || !past) && hidden) this.follow();
        if (down && past) this.classList.add('is-hidden');
        else if (up || !past) this.classList.remove('is-hidden');
      }
      if (Math.abs(y - this.lastY) > 2) this.lastY = y;
      this.updateOffset();
    });
  }

  /* The bar slides in and out; --header-offset has to stay on its edge while it travels,
     because no scroll event fires once the finger stops. (Same trick as <mw-header>.) */
  follow() {
    const style = getComputedStyle(this);
    const seconds = parseFloat(style.transitionDuration) || 0;
    this.followUntil = performance.now() + seconds * 1000 + 60;
    if (this.following) return;
    this.following = true;
    const step = () => {
      this.updateOffset();
      if (this.isConnected && performance.now() < this.followUntil) requestAnimationFrame(step);
      else this.following = false;
    };
    requestAnimationFrame(step);
  }
}
if (!customElements.get('mw-mobile-header')) customElements.define('mw-mobile-header', MwMobileHeader);

/* ---------- Mobile menu: a stack of panels inside one overlay ---------- */
class MwMobileMenu extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog.mw-mmenu');
    this.stack = this.querySelector('[data-mw-menu-stack]');
    if (!this.dialog || !this.stack) return;
    this.panels = [...this.stack.querySelectorAll('.mw-mmenu__panel')];
    this.rootPanel = this.panels.find((panel) => panel.dataset.level === 'root');
    this.path = [];

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onOverlayClose = this.onOverlayClose.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);

    this.addEventListener('click', this.onClick);
    this.dialog.addEventListener('keydown', this.onKeydown);
    document.addEventListener('mw:overlay:close', this.onOverlayClose);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    this.show(this.rootPanel, { focus: false });
  }

  disconnectedCallback() {
    document.removeEventListener('mw:overlay:close', this.onOverlayClose);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  panelById(id) {
    return this.panels.find((panel) => panel.id === id) || null;
  }

  onClick(event) {
    const opener = event.target.closest('[data-mw-menu-open]');
    if (opener && this.contains(opener)) {
      const panel = this.panelById(opener.getAttribute('data-mw-menu-open'));
      if (panel) {
        event.preventDefault();
        this.open(panel, opener);
      }
      return;
    }
    const back = event.target.closest('[data-mw-menu-back]');
    if (back && this.contains(back)) {
      event.preventDefault();
      this.back();
    }
  }

  /* Escape steps back through the panels before the overlay closes (overlay.js owns the last
     step — the native dialog close — so focus return keeps working). */
  onKeydown(event) {
    if (event.key !== 'Escape' || !this.path.length) return;
    event.preventDefault();
    this.back();
  }

  onOverlayClose(event) {
    if (event.target === this.dialog) this.reset();
  }

  onBlockSelect(event) {
    const panel = event.target.closest?.('.mw-mmenu__panel');
    if (panel && this.panels.includes(panel)) this.show(panel, { focus: false });
  }

  open(panel, opener) {
    this.path.push({ panel, opener });
    opener?.setAttribute('aria-expanded', 'true');
    this.show(panel);
  }

  back() {
    const previous = this.path.pop();
    previous?.opener?.setAttribute('aria-expanded', 'false');
    const target = this.path.at(-1)?.panel || this.rootPanel;
    this.show(target);
    previous?.opener?.focus({ preventScroll: true });
  }

  reset() {
    this.path.forEach(({ opener }) => opener?.setAttribute('aria-expanded', 'false'));
    this.path = [];
    this.show(this.rootPanel, { focus: false });
  }

  show(panel, { focus = true } = {}) {
    if (!panel) return;
    this.panels.forEach((other) => {
      const active = other === panel;
      other.classList.toggle('is-active', active);
      other.inert = !active;
    });
    this.dialog.querySelector('.overlay__body')?.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    if (!focus) return;
    const first = panel.querySelector('[data-mw-menu-back], a[href], button:not([disabled])');
    first?.focus({ preventScroll: true });
  }
}
if (!customElements.get('mw-mobile-menu')) customElements.define('mw-mobile-menu', MwMobileMenu);

/* ---------- Mobile dock ---------- */
class MwMobileDock extends HTMLElement {
  connectedCallback() {
    this.onScroll = this.onScroll.bind(this);
    this.lastY = window.scrollY;
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this);
    this.measure();
    if (this.dataset.visibility === 'scroll') {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    } else {
      this.classList.add('is-shown');
    }
    this.watchHero();
  }

  /* A page that opens on a slideshow keeps the dock away while the hero fills the screen
     (Omar 22.10): it shows once the hero's bottom edge is in the top third of the viewport. */
  watchHero() {
    if (!this.hasAttribute('data-hide-on-hero')) return;
    const first = document.querySelector('#MainContent > .shopify-section');
    if (!first?.classList.contains('section-slideshow')) return;
    this.hero = first;
    this.onHero = () => {
      this.classList.toggle('is-over-hero', this.hero.getBoundingClientRect().bottom > window.innerHeight / 3);
    };
    window.addEventListener('scroll', this.onHero, { passive: true });
    window.addEventListener('resize', this.onHero, { passive: true });
    this.onHero();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    if (this.onHero) {
      window.removeEventListener('scroll', this.onHero);
      window.removeEventListener('resize', this.onHero);
    }
    /* Removed in the theme editor, or above the breakpoint: the page keeps no room for it. */
    if (!document.querySelector('mw-mobile-dock')) setPx('--mw-dock-h', 0);
  }

  /* offsetHeight is 0 while the section's own media query hides the dock, which is exactly the
     value the page should reserve then. */
  measure() {
    setPx('--mw-dock-h', this.offsetHeight);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      const y = Math.max(0, window.scrollY);
      const down = y > this.lastY + 4;
      const up = y < this.lastY - 4;
      /* Down hides it, up brings it back — the same way round as the sticky header, and what
         Helix does (Omar, 20.09). The dock is there at the top of the page and gets out of the
         way while the shopper reads on; the first upward flick returns it. */
      if (down && y > 120) this.classList.remove('is-shown');
      else if (up || y <= 120) this.classList.add('is-shown');
      if (Math.abs(y - this.lastY) > 2) this.lastY = y;
    });
  }
}
if (!customElements.get('mw-mobile-dock')) customElements.define('mw-mobile-dock', MwMobileDock);

/* ---------- Shared triggers ----------
   Search: a plain link today. The moment the theme gets a search overlay (a
   dialog.overlay[data-mw-search-panel]) both the header icon and the dock item open it
   instead of navigating — one panel, two triggers. */
const searchPanel = () => {
  const panel = document.querySelector('dialog.overlay[data-mw-search-panel]');
  return panel?.id ? panel : null;
};

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const search = target.closest('[data-mw-search]');
  if (search) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const panel = searchPanel();
    if (panel && window.Munkwell?.overlay?.open(panel.id, search)) event.preventDefault();
    return;
  }

  /* Menu: overlay.js opens #MobileMenu on its own. Only when that section is missing does the
     dock fall back to the header's legacy drawer, so there is never a second menu. */
  const menu = target.closest('[data-mw-menu-toggle]');
  if (!menu || document.getElementById('MobileMenu')) return;
  const legacy = document.getElementById('MenuDrawer');
  if (!(legacy instanceof HTMLDialogElement) || legacy.open) return;
  event.preventDefault();
  legacy.showModal();
  menu.setAttribute('aria-expanded', 'true');
  legacy.addEventListener('close', () => menu.setAttribute('aria-expanded', 'false'), { once: true });
});

export { MwMobileHeader, MwMobileMenu, MwMobileDock };
